/**
 * Admin Portal (web): "Store access" — partner codes platform C4 (2026-09-27).
 *
 * Where the owner gives the server access to both stores, once, so it can create the partner
 * discount offers and the App Store one-time-code supply itself (checklist step
 * p6-store-access). The page walks through creating an App Store Connect API key and a Google
 * Play service-account key, then uploads each. Keys go to AWS Secrets Manager and are never
 * shown again: the fields are write-only; the card shows what is saved ("set on <date> by
 * <admin>"), the non-secret ids, and the last "Test connection" result. Below: the App Store
 * one-time code inventory (counts only — never a code). Codes PR-6: "What the stores sell" reads
 * every product's offers from App Store Connect and Play Console (read-only) and shows what does
 * not match Coach Cedar's records; the slot meter names each slot's owner.
 *
 * super_admin only (the server refuses everyone else with 403). Backend:
 * routers/admin_store_access.py. Decisions: components/admin/storeAccessLogic.ts.
 * Web-only (the admin portal is not part of the native app).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, KeyRound, RefreshCw, Store } from 'lucide-react';
import { AdminApiError, adminFetch } from '../../../utils/adminApi';
import {
  BUCKET_STATE_TONE,
  FINDING_TITLES,
  acknowledgeAction,
  SLOT_EXPLAINER,
  STORE_TITLES,
  activeCountLine,
  appStoreFormError,
  appStorePricePointHint,
  bucketFormError,
  codeWords,
  emptyInventoryText,
  googlePlayFormError,
  identifierRows,
  lastRefreshLine,
  levelRows,
  offerLengthText,
  pickableTerms,
  productPeriod,
  quotaLine,
  refreshResultText,
  refusalText,
  setLine,
  slotHolderLabel,
  slotLine,
  slotsFor,
  storeChip,
  pickableLevels,
  termOptionLabel,
  testLine,
  type BucketsView,
  type InventoryView,
  type ProductSlots,
  type SlotHolder,
  type StoreKey,
  type StoreTruthView,
  type StoreView,
  type Tone,
} from '../../../components/admin/storeAccessLogic';
import {
  SHORT_TERM_NOTICE,
  coverageHasShortTerm,
  coverageSummary,
  tagCoverageWarnings,
  termLabel,
  type DiscountCoverage,
  type Period,
} from '../../../components/admin/discountTermsLogic';

const BASE = '/api/admin/store-access';
const GREEN = '#1F6C4F';

// Through the shared admin helper (utils/adminApi.ts: the session token, and no request at
// all without one), never a hand-rolled Authorization header (test_raw_bearer_ratchet).
async function call(path: string, init?: RequestInit): Promise<{ status: number; body: any }> {
  try {
    return { status: 200, body: await adminFetch<any>(`${BASE}${path}`, init) };
  } catch (err) {
    if (err instanceof AdminApiError) {
      let body: any = null;
      try {
        body = err.body ? JSON.parse(err.body) : null;
      } catch {
        body = null;
      }
      return { status: err.status, body };
    }
    // A non-JSON 200 is the SPA fallback page, not an answer: never read it as success.
    return { status: 502, body: null };
  }
}

const TONE_CLASSES: Record<Tone, string> = {
  grey: 'bg-gray-100 text-gray-700',
  amber: 'bg-amber-100 text-amber-800',
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-800',
};

function Chip({ label, tone }: { label: string; tone: Tone }) {
  return <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${TONE_CLASSES[tone]}`}>{label}</span>;
}

function Steps({ title, steps }: { title: string; steps: React.ReactNode[] }) {
  return (
    <details className="mb-4 text-sm text-gray-700">
      <summary className="cursor-pointer font-medium" style={{ color: GREEN }}>{title}</summary>
      <ol className="list-decimal ml-5 mt-2 space-y-1">
        {steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
    </details>
  );
}

const APPLE_STEPS: React.ReactNode[] = [
  <>Open <a className="underline" href="https://appstoreconnect.apple.com/access/integrations/api" target="_blank" rel="noreferrer">App Store Connect → Users and Access → Integrations → App Store Connect API</a> (Team Keys). The first time, the Account Holder presses <b>Request Access</b> and accepts the terms.</>,
  <>Press <b>+</b> (Generate API Key). Name: <code>Coach Cedar server — partner codes</code>. Access: <b>App Manager</b>. Press <b>Generate</b>.</>,
  <>Copy the <b>Issuer ID</b> (above the list of keys) and the new key's <b>Key ID</b> into the form below.</>,
  <>In the key's row press <b>Download</b>. Apple lets you download it only once — the file is <code>AuthKey_&lt;Key ID&gt;.p8</code>. Choose that file below.</>,
  <>Press <b>Save</b>, then <b>Test connection</b>. After a successful save you may delete the .p8 from your Downloads folder — the server has it.</>,
];

const GOOGLE_STEPS: React.ReactNode[] = [
  <>Open <a className="underline" href="https://console.cloud.google.com/apis/library/androidpublisher.googleapis.com" target="_blank" rel="noreferrer">Google Cloud → Google Play Android Developer API</a>, pick (or create) a project for Coach Cedar, and press <b>Enable</b>.</>,
  <>Go to <b>IAM &amp; Admin → Service Accounts → Create service account</b>. Name: <code>cedar-play-partner-offers</code>. Press <b>Create and continue</b>, skip the roles, press <b>Done</b>.</>,
  <>Open the new account → <b>Keys</b> tab → <b>Add key → Create new key → JSON → Create</b>. A .json file downloads. (If Google says key creation is blocked by an organization policy, stop and tell the agent.)</>,
  <>Copy the account's email (it ends in <code>.iam.gserviceaccount.com</code>). Open <a className="underline" href="https://play.google.com/console" target="_blank" rel="noreferrer">Play Console</a> → <b>Users and permissions → Invite new users</b>, paste the email, then <b>App permissions → Add app → Coach Cedar</b> and tick <b>Manage store presence</b> (it covers the subscription offers). Leave every other permission unticked — least privilege; if Play ticks the read-only <b>View app information</b> for you, that one is fine. Press <b>Invite user</b>.</>,
  <>Choose the .json file below, press <b>Save</b>, then <b>Test connection</b>. A brand-new invite can take a few hours before Google lets the account in; if the test says it is not allowed, test again later.</>,
];

async function readFileText(file: File | undefined | null): Promise<string> {
  if (!file) return '';
  return file.text();
}

function StoreCard({ store, view, forbidden, onSaved }: {
  store: StoreKey; view: StoreView | undefined; forbidden: boolean; onSaved: () => void;
}) {
  const [issuerId, setIssuerId] = useState('');
  const [keyId, setKeyId] = useState('');
  const [appId, setAppId] = useState('6758776684');
  const [packageName, setPackageName] = useState('com.coachcedar.app');
  // The secret half lives in component state only until it is sent, then it is cleared.
  const [secret, setSecret] = useState('');
  const [secretName, setSecretName] = useState('');
  const [paste, setPaste] = useState(false);
  // Bumped after a send so the file picker is re-created empty (it shows no stale file name).
  const [fileKey, setFileKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const chip = storeChip(view);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setSecret(await readFileText(file));
    setSecretName(file?.name ?? '');
  };

  const save = async () => {
    setMessage(null);
    const localError = store === 'app_store'
      ? appStoreFormError({ issuerId, keyId, appId, privateKey: secret })
      : googlePlayFormError({ serviceAccountJson: secret, packageName });
    if (localError) {
      setMessage({ ok: false, text: localError });
      return;
    }
    const body = store === 'app_store'
      ? { issuer_id: issuerId.trim(), key_id: keyId.trim().toUpperCase(), app_id: appId.trim(), private_key: secret }
      : { service_account_json: secret, package_name: packageName.trim() };
    setBusy(true);
    const path = store === 'app_store' ? '/app-store' : '/google-play';
    const { status, body: res } = await call(path, { method: 'PUT', body: JSON.stringify(body) });
    setBusy(false);
    setSecret('');
    setSecretName('');
    setFileKey((k) => k + 1);
    if (status === 200 && res?.success) {
      setMessage({ ok: true, text: 'Saved. Press Test connection to check it works.' });
      onSaved();
    } else {
      setMessage({ ok: false, text: refusalText(res, 'Could not save. Nothing was changed.') });
    }
  };

  const test = async () => {
    setMessage(null);
    setBusy(true);
    const path = store === 'app_store' ? '/app-store/test' : '/google-play/test';
    const { status, body: res } = await call(path, { method: 'POST' });
    setBusy(false);
    if (status === 200 && res?.success) {
      setMessage({ ok: !!res.ok, text: codeWords(res.code) });
      onSaved();
    } else {
      setMessage({ ok: false, text: refusalText(res, 'Could not run the test.') });
    }
  };

  const set = setLine(view);
  const tested = testLine(view);
  const input = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg';

  return (
    <div className="p-5 bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        <KeyRound className="h-5 w-5" style={{ color: GREEN }} />
        <h2 className="text-lg font-semibold" style={{ color: '#0B1614' }}>{STORE_TITLES[store]}</h2>
        <Chip {...chip} />
      </div>
      {set && <p className="text-sm text-gray-700">{set}</p>}
      {tested && <p className="text-sm text-gray-600 mb-2">{tested}</p>}
      {identifierRows(view).length > 0 && (
        <dl className="text-sm grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 mb-3">
          {identifierRows(view).map((r) => (
            <React.Fragment key={r.label}>
              <dt className="text-gray-500">{r.label}</dt>
              <dd className="font-mono text-xs break-all">{r.value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}

      <Steps title={store === 'app_store' ? 'How to create the App Store Connect key (5 steps)' : 'How to create the Google Play key (5 steps)'}
        steps={store === 'app_store' ? APPLE_STEPS : GOOGLE_STEPS} />

      {!forbidden && (
        <div className="space-y-2">
          {store === 'app_store' ? (
            <>
              <input className={input} placeholder="Issuer ID" value={issuerId} onChange={(e) => setIssuerId(e.target.value)} autoComplete="off" />
              <input className={input} placeholder="Key ID (10 characters)" value={keyId} onChange={(e) => setKeyId(e.target.value)} autoComplete="off" />
              <input className={input} placeholder="Apple app ID" value={appId} onChange={(e) => setAppId(e.target.value)} autoComplete="off" />
            </>
          ) : (
            <input className={input} placeholder="Package name" value={packageName} onChange={(e) => setPackageName(e.target.value)} autoComplete="off" />
          )}
          <div className="text-sm">
            {paste ? (
              <textarea className={`${input} font-mono text-xs h-28`} autoComplete="off" spellCheck={false}
                placeholder={store === 'app_store' ? 'Paste the whole .p8 file, BEGIN and END lines included' : 'Paste the whole .json key file'}
                value={secret} onChange={(e) => { setSecret(e.target.value); setSecretName(''); }} />
            ) : (
              <label className="flex items-center gap-2">
                <span className="text-gray-700">{store === 'app_store' ? 'Key file (.p8):' : 'Key file (.json):'}</span>
                <input key={fileKey} type="file" accept={store === 'app_store' ? '.p8' : '.json,application/json'} onChange={(e) => void onFile(e)} />
              </label>
            )}
            <button type="button" className="mt-1 text-xs underline text-gray-500" onClick={() => { setPaste(!paste); setSecret(''); setSecretName(''); }}>
              {paste ? 'Choose a file instead' : 'Paste it instead'}
            </button>
            {secretName && <span className="ml-2 text-xs text-gray-500">{secretName} ready — press Save.</span>}
          </div>
          <p className="text-xs text-gray-500">
            The key is stored in AWS Secrets Manager ({view?.secret_name ?? '—'}) and is never shown again.
            Saving again replaces it.
          </p>
          <div className="flex items-center gap-3">
            <button onClick={() => void save()} disabled={busy || !secret}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: GREEN }}>
              {view?.configured ? 'Replace key' : 'Save'}
            </button>
            <button onClick={() => void test()} disabled={busy || !view?.configured}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              Test connection
            </button>
            {busy && <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />}
          </div>
        </div>
      )}
      {message && (
        <div className={`mt-3 p-3 rounded-lg flex items-start gap-2 ${message.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          {message.ok ? <CheckCircle2 className="h-4 w-4 text-green-700 mt-0.5" /> : <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />}
          <span className={`text-sm ${message.ok ? 'text-green-900' : 'text-red-800'}`}>{message.text}</span>
        </div>
      )}
    </div>
  );
}

function slotTone(slot: ProductSlots): Tone {
  if (slot.free <= 0) return 'red';
  if (slot.free <= 1) return 'amber';
  return 'green';
}

/** Discounts partner links promise that some store product has no live offer for (design finding #3). */
function CoverageWarnings() {
  const [coverage, setCoverage] = useState<DiscountCoverage | undefined>(undefined);
  useEffect(() => {
    adminFetch<any>('/api/admin/partners/discount-coverage')
      .then((b) => { if (b?.success) setCoverage(b as DiscountCoverage); })
      .catch(() => setCoverage(undefined));
  }, []);
  const summary = coverageSummary(coverage);
  // A live tag whose monthly discount lasts less than 12 months reaches only the newer app
  // builds (the old-build gate in services/discount_offers) — say so here too (L-D).
  const shortTerm = coverageHasShortTerm(coverage);
  if (!coverage || (!summary && !shortTerm)) return null;
  return (
    <>
      {shortTerm && (
        <p className="mb-3 text-sm text-amber-800" data-testid="short-term-notice">{SHORT_TERM_NOTICE}</p>
      )}
      {summary && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="discount-coverage-warning">
          <p className="text-sm font-medium text-amber-900 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {summary}
          </p>
          <ul className="mt-2 space-y-1">
            {coverage.tags.filter((t) => !t.buyable_everywhere).map((t) => (
              <li key={t.tag_id} className="text-sm text-amber-800">
                <b>{t.partner_code} · {t.tag}</b>: {tagCoverageWarnings(t).join(' ')}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function DiscountLevels({ disabled, reloadKey }: { disabled: boolean; reloadKey: number }) {
  const [view, setView] = useState<BucketsView | undefined>(undefined);
  const [level, setLevel] = useState('');
  const [months, setMonths] = useState('12');
  const [store, setStore] = useState<StoreKey>('app_store');
  const [productId, setProductId] = useState('');
  const [offerId, setOfferId] = useState('');
  const [state, setState] = useState('active');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    const { status, body } = await call('/buckets');
    if (status === 200 && body?.success) setView(body as BucketsView);
  }, []);
  // Reloads after "Refresh store offers" (reloadKey) too: the meter reads what the stores sell.
  useEffect(() => { void load(); }, [load, reloadKey]);

  const period = productPeriod(view, store, productId);
  const terms = pickableTerms(view, level, store, productId);
  // An annual product is always the first year; a length this level doesn't offer falls back to
  // the longest it does.
  const term = period === 'annual' ? '12'
    : (terms.includes(Number(months)) ? months : String(terms[terms.length - 1] ?? 12));

  const save = async () => {
    setMessage(null);
    const err = bucketFormError({ level, store, productId, offerId, state, months: term }, view);
    if (err) {
      setMessage({ ok: false, text: err });
      return;
    }
    setBusy(true);
    const { status, body } = await call('/buckets', {
      method: 'PUT',
      body: JSON.stringify({ level_pct: Number(level), months: Number(term), store, product_id: productId,
        store_offer_id: offerId.trim(), state }),
    });
    setBusy(false);
    if (status === 200 && body?.success) {
      const b = body.bucket ?? {};
      setMessage({ ok: true, text: b.offer_changed
        ? `Saved. The offer changed: ${b.retired_codes ?? 0} unused codes of the old offer retired, ${b.failed_batches ?? 0} batch(es) stopped.`
        : 'Saved.' });
      void load();
    } else {
      setMessage({ ok: false, text: refusalText(body, 'Could not save. Nothing was changed.') });
    }
  };

  const input = 'px-3 py-2 text-sm border border-gray-300 rounded-lg';
  const products = view?.products?.[store] ?? [];
  const count = activeCountLine(view, store, productId);
  const pricePointHint = appStorePricePointHint(level, store, Number(term), period);
  const selectedSlot = productId ? slotsFor(view, store, productId) : undefined;
  return (
    <div className="mt-8 p-5 bg-white border border-gray-200 rounded-lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: '#0B1614' }}>Discount offers</h2>
      <p className="text-sm text-gray-500 mb-3">
        One row per discount, length and store product: the offer the store holds for it. A monthly discount lasts the
        first 1, 3, 6 or 12 months; an annual discount always lasts the first year. The stores fix an offer’s length
        when it is created, so each discount-and-length pair is its own store offer: create it in the store (App Store
        offer code offer, or Google Play developer-determined offer) at that percentage and length, then record its id
        here and switch it on. A pair offered by a live partner link can’t be switched off. Changing an offer id
        retires the unused App Store codes minted for the old offer. Leaving the offer id empty when switching an offer
        off keeps the one stored.
      </p>
      <p className="text-sm text-gray-600 mb-3" data-testid="slot-explainer">{SLOT_EXPLAINER}</p>
      <p className="text-sm text-amber-800 mb-3">
        Switching an offer off here does not deactivate it in App Store Connect or Play Console — do that in the
        store console too.
      </p>
      <CoverageWarnings />
      {(view?.slots ?? []).length > 0 && (
        <table className="w-full text-sm mb-4" data-testid="slot-meters">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 font-medium">Store</th>
              <th className="py-1 font-medium">Product</th>
              <th className="py-1 font-medium">In use</th>
              <th className="py-1 font-medium text-right">Slots</th>
            </tr>
          </thead>
          <tbody>
            {(view?.slots ?? []).map((sl) => (
              <tr key={`${sl.store}:${sl.product_id}`} className="border-t border-gray-100">
                <td className="py-1">{STORE_TITLES[sl.store]}</td>
                <td className="py-1"><code className="text-xs">{sl.product_id}</code>{sl.period ? ` (${sl.period})` : ''}</td>
                <td className="py-1 text-xs text-gray-700" data-testid="slot-owners">
                  {sl.owners
                    ? (sl.owners.map(slotHolderLabel).join(' · ') || '—')
                    : ([...sl.ours.map((o) => `${o.level_pct}% · ${termLabel((sl.period as Period) || 'monthly', o.months)}`),
                      ...sl.external.map((e) => `${e.name} (${e.owner})`)].join(' · ') || '—')}
                  <div className="text-gray-500">{slotLine(sl)}</div>
                </td>
                <td className="py-1 text-right"><Chip label={`${sl.used} / ${sl.cap}`} tone={slotTone(sl)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(view?.buckets ?? []).length > 0 && (
        <table className="w-full text-sm mb-4">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 font-medium">Discount</th>
              <th className="py-1 font-medium">Length</th>
              <th className="py-1 font-medium">Store</th>
              <th className="py-1 font-medium">Product</th>
              <th className="py-1 font-medium">Offer id</th>
              <th className="py-1 font-medium text-right">State</th>
            </tr>
          </thead>
          <tbody>
            {(view?.buckets ?? []).map((b) => (
              <tr key={b.id} className="border-t border-gray-100">
                <td className="py-1">{b.level_pct}%</td>
                <td className="py-1">{termLabel(b.period, b.months)}</td>
                <td className="py-1">{STORE_TITLES[b.store]}</td>
                <td className="py-1"><code className="text-xs">{b.product_id}</code> ({b.period})</td>
                <td className="py-1"><code className="text-xs">{b.store_offer_id ?? '—'}</code></td>
                <td className="py-1 text-right"><Chip label={b.state} tone={BUCKET_STATE_TONE[b.state]} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!disabled && (
        <div className="flex flex-wrap items-center gap-2">
          <select className={input} value={level} onChange={(e) => setLevel(e.target.value)} aria-label="Discount level">
            <option value="">Level…</option>
            {pickableLevels(view).map((p) => <option key={p} value={String(p)}>{p}% off</option>)}
          </select>
          <select className={input} value={store} onChange={(e) => { setStore(e.target.value as StoreKey); setProductId(''); }}
            aria-label="Store">
            <option value="app_store">App Store</option>
            <option value="google_play">Google Play</option>
          </select>
          <select className={input} value={productId} onChange={(e) => setProductId(e.target.value)} aria-label="Product">
            <option value="">Product…</option>
            {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.product_id} ({p.period})</option>)}
          </select>
          <select className={input} value={term} onChange={(e) => setMonths(e.target.value)} aria-label="Length"
            disabled={period === 'annual'}>
            {(terms.length ? terms : [12]).map((m) => (
              <option key={m} value={String(m)}>{termOptionLabel(m, period)}</option>
            ))}
          </select>
          <input className={input} placeholder="Store offer id" value={offerId} onChange={(e) => setOfferId(e.target.value)} autoComplete="off" />
          <select className={input} value={state} onChange={(e) => setState(e.target.value)} aria-label="State">
            <option value="active">active</option>
            <option value="pending">pending</option>
            <option value="inactive">inactive</option>
          </select>
          <button onClick={() => void save()} disabled={busy}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: GREEN }}>
            Save offer
          </button>
          {count && <span className={`text-xs ${selectedSlot && selectedSlot.free <= 0 ? 'text-red-700' : 'text-gray-500'}`}>{count}</span>}
        </div>
      )}
      {!disabled && pricePointHint && <p className="mt-2 text-xs text-gray-600">{pricePointHint}</p>}
      {message && <p className={`mt-2 text-sm ${message.ok ? 'text-green-800' : 'text-red-800'}`}>{message.text}</p>}
    </div>
  );
}

/** Acknowledge an unrecorded store offer as Coach Cedar's own (it keeps its slot), or take that back. */
function AcknowledgeControl({ offer, store, productId, disabled, onDone }: {
  offer: SlotHolder; store: StoreKey; productId: string; disabled: boolean; onDone: (text: string) => void;
}) {
  const action = acknowledgeAction(offer);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(offer.reference_name ?? offer.store_offer_id ?? '');
  const [busy, setBusy] = useState(false);
  if (!action || disabled) return null;
  const send = async (acknowledged: boolean) => {
    setBusy(true);
    const { status, body } = await call('/store-offers/acknowledgements', {
      method: 'POST',
      body: JSON.stringify({ store, product_id: productId, base_plan_id: offer.base_plan_id ?? '',
        store_offer_id: offer.store_offer_id, label: label.trim(), acknowledged }),
    });
    setBusy(false);
    setOpen(false);
    onDone(status === 200 && body?.success
      ? (acknowledged ? 'Acknowledged: it keeps its slot and is no longer flagged.' : 'Acknowledgement removed.')
      : refusalText(body, 'Could not save. Nothing was changed.'));
  };
  if (action === 'undo') {
    return (
      <button onClick={() => void send(false)} disabled={busy} className="text-xs underline text-gray-600">Undo</button>
    );
  }
  return open ? (
    <span className="inline-flex items-center gap-1">
      <input className="px-2 py-1 text-xs border border-gray-300 rounded" value={label} maxLength={200}
        onChange={(e) => setLabel(e.target.value)} aria-label="What this offer is" />
      <button onClick={() => void send(true)} disabled={busy || !label.trim()} className="text-xs underline" style={{ color: GREEN }}>
        Save
      </button>
    </span>
  ) : (
    <button onClick={() => setOpen(true)} className="text-xs underline" style={{ color: GREEN }}>
      Acknowledge as Coach Cedar&apos;s
    </button>
  );
}

/** "What the stores sell" (Codes PR-6): what App Store Connect and Play Console list, read-only. */
function StoreTruth({ onRefreshed, writesDisabled }: { onRefreshed: () => void; writesDisabled: boolean }) {
  const [view, setView] = useState<StoreTruthView | undefined>(undefined);
  const [pending, setPending] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { status, body } = await call('/store-offers');
    if (status === 200 && body?.success) {
      setView(body as StoreTruthView);
      setPending(null);
    } else {
      setPending(refusalText(body, 'Could not read what the stores sell.'));
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    setMessage(null);
    const { status, body } = await call('/store-offers/refresh', { method: 'POST' });
    setBusy(false);
    setMessage(status === 200 && body?.success ? refreshResultText(body.summary)
      : refusalText(body, 'Reading the stores failed. Nothing was changed.'));
    void load();
    onRefreshed();
  };
  const acknowledged = (text: string) => {
    setMessage(text);
    void load();
    onRefreshed();
  };

  const offers = (view?.products ?? []).flatMap((p) => p.offers.map((o) => ({ ...o, store: p.store, productId: p.product_id })));
  return (
    <div className="mt-8 p-5 bg-white border border-gray-200 rounded-lg" data-testid="store-truth">
      <div className="flex items-center gap-3 mb-1">
        <h2 className="text-lg font-semibold" style={{ color: '#0B1614' }}>What the stores sell</h2>
        <button onClick={() => void refresh()} disabled={busy || view?.switches?.store_offer_refresh === false}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
          Refresh store offers
        </button>
        {busy && <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />}
      </div>
      <p className="text-sm text-gray-500 mb-3">
        Reads every Coach Cedar subscription&apos;s offers from App Store Connect and Play Console and compares them with
        the discount offers recorded above. It only reads: nothing in either store is created, changed or switched off.
        For {view?.max_age_days ?? 7} days after a product is read, its slot meter above and the switch-on check count
        the store&apos;s own offers; after that they go back to what Coach Cedar recorded until it is read again.
      </p>
      {pending && <p className="text-sm text-gray-600 mb-3">{pending}</p>}
      {view && <p className="text-sm text-gray-700 mb-3" data-testid="store-truth-last">{lastRefreshLine(view)}</p>}
      {message && <p className="text-sm text-gray-800 mb-3">{message}</p>}
      {(view?.findings ?? []).length > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="store-truth-findings">
          <ul className="space-y-2">
            {(view?.findings ?? []).map((f, i) => (
              <li key={`${f.kind}:${f.store}:${f.product_id}:${f.store_offer_id ?? i}`} className="text-sm text-amber-900">
                <b>{FINDING_TITLES[f.kind] ?? f.kind}</b> — {STORE_TITLES[f.store]} · <code className="text-xs">{f.product_id}</code>
                <div className="text-amber-800">{f.message}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {offers.length > 0 && (
        <table className="w-full text-sm" data-testid="store-truth-offers">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 font-medium">Store</th>
              <th className="py-1 font-medium">Product</th>
              <th className="py-1 font-medium">Offer</th>
              <th className="py-1 font-medium">Owner</th>
              <th className="py-1 font-medium">Length</th>
              <th className="py-1 font-medium text-right">In the store</th>
            </tr>
          </thead>
          <tbody>
            {offers.map((o) => (
              <tr key={`${o.store}:${o.productId}:${o.base_plan_id ?? ''}:${o.store_offer_id}`} className="border-t border-gray-100">
                <td className="py-1">{STORE_TITLES[o.store]}</td>
                <td className="py-1"><code className="text-xs">{o.productId}</code></td>
                <td className="py-1"><code className="text-xs">{o.reference_name ?? o.store_offer_id}</code></td>
                <td className="py-1 text-xs">
                  {slotHolderLabel(o)}{' '}
                  <AcknowledgeControl offer={o} store={o.store} productId={o.productId} disabled={writesDisabled}
                    onDone={acknowledged} />
                </td>
                <td className="py-1">{offerLengthText(o.duration_months)}</td>
                <td className="py-1 text-right">
                  <Chip label={o.state ?? 'unknown'} tone={o.state === 'active' ? 'green' : o.state === 'draft' ? 'amber' : 'grey'} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function AdminStoreAccess(): React.ReactElement {
  const [stores, setStores] = useState<Record<string, StoreView>>({});
  const [inventory, setInventory] = useState<InventoryView | undefined>(undefined);
  const [switches, setSwitches] = useState<{ store_access_admin?: boolean; apple_code_inventory?: boolean }>({});
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [runMessage, setRunMessage] = useState<string | null>(null);
  const [slotsReload, setSlotsReload] = useState(0);

  const load = useCallback(async () => {
    const { status, body } = await call('/status');
    if (status === 403) {
      setForbidden(true);
      return;
    }
    if (status !== 200 || !body?.success) {
      setError(refusalText(body, 'Could not read the store access status.'));
      return;
    }
    setError(null);
    setStores(body.stores ?? {});
    setInventory(body.inventory);
    setSwitches(body.switches ?? {});
  }, []);

  useEffect(() => { void load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    setRunMessage(null);
    const { status, body } = await call('/inventory/run', { method: 'POST' });
    setRunning(false);
    if (status === 200 && body?.success) {
      const s = body.summary ?? {};
      const levels = Array.isArray(s.levels) ? s.levels : [];
      const requested = levels.reduce((n: number, l: any) => n + (l.requested || 0), 0);
      const imported = levels.reduce((n: number, l: any) => n + (l.imported || 0), 0);
      setRunMessage(s.status === 'busy' ? 'Another run is already going; try again in a minute.'
        : s.status === 'not_configured' ? 'A discount level is live but no working App Store key is saved.'
          : `Checked ${levels.length} level(s): ${requested} codes asked of Apple, ${imported} imported.`);
      void load();
    } else {
      setRunMessage(refusalText(body, 'The inventory run failed.'));
    }
  };

  const empty = emptyInventoryText(inventory, stores.app_store);
  const rows = levelRows(inventory);

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center gap-2 mb-1">
        <Store className="h-6 w-6" style={{ color: GREEN }} />
        <h1 className="text-2xl font-semibold" style={{ color: '#0B1614' }}>Store access</h1>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Gives the server access to the App Store and Google Play, once, so it creates the partner discount offers and the
        App Store code supply itself — nobody clicks through the store consoles for each partner. Keys are uploaded here,
        stored in AWS Secrets Manager, and never shown again.
      </p>
      {forbidden && <p className="text-sm text-gray-700">Only a super admin can open this page.</p>}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <span className="text-sm text-red-800">{error}</span>
        </div>
      )}
      {switches.store_access_admin === false && (
        <p className="mb-4 text-sm text-amber-800">Saving and testing keys is switched off (FF_STORE_ACCESS_ADMIN).</p>
      )}
      {!forbidden && (
        <>
          <div className="grid gap-6 md:grid-cols-2">
            <StoreCard store="app_store" view={stores.app_store} forbidden={forbidden} onSaved={() => void load()} />
            <StoreCard store="google_play" view={stores.google_play} forbidden={forbidden} onSaved={() => void load()} />
          </div>

          <DiscountLevels disabled={switches.store_access_admin === false} reloadKey={slotsReload} />

          <StoreTruth onRefreshed={() => setSlotsReload((n) => n + 1)}
            writesDisabled={switches.store_access_admin === false} />

          <div className="mt-8 p-5 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-semibold" style={{ color: '#0B1614' }}>App Store one-time codes</h2>
              <button onClick={() => void runNow()} disabled={running || switches.apple_code_inventory === false}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                Run inventory check now
              </button>
              {running && <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />}
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Each live App Store discount level keeps at least {inventory?.floor ?? 200} unused codes. The server asks
              Apple for a new batch when a level runs low (checked every 6 hours) and retires codes
              {` ${inventory?.retire_before_days ?? 14}`} days before they expire. Codes themselves are never shown.
            </p>
            {runMessage && <p className="text-sm text-gray-800 mb-3">{runMessage}</p>}
            {quotaLine(inventory) && <p className="text-sm text-gray-700 mb-3">{quotaLine(inventory)}</p>}
            {empty && <p className="text-sm text-gray-600 mb-3">{empty}</p>}
            {rows.length > 0 && (
              <table className="w-full text-sm mb-3">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-1 font-medium">Discount level</th>
                    <th className="py-1 font-medium text-right">Unused</th>
                    <th className="py-1 font-medium text-right">Handed out</th>
                    <th className="py-1 font-medium text-right">Redeemed</th>
                    <th className="py-1 font-medium text-right">State</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key} className="border-t border-gray-100">
                      <td className="py-1">{r.name}</td>
                      <td className="py-1 text-right">{r.usable}</td>
                      <td className="py-1 text-right">{r.allocated}</td>
                      <td className="py-1 text-right">{r.redeemed}</td>
                      <td className="py-1 text-right"><Chip label={r.state} tone={r.tone} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(inventory?.alerts ?? []).length > 0 && (
              <div className="space-y-2">
                {(inventory?.alerts ?? []).map((a) => (
                  <div key={a.id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-medium text-amber-900">{a.title}</p>
                    <p className="text-sm text-amber-800">{a.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AdminStoreAccess;
